import { clsx, type ClassValue } from 'clsx';
import { Readable } from 'stream';
import { twMerge } from 'tailwind-merge';

import type { DanishAddress } from '@/db/models/company_profile';

export function presentableFileSize(size: number): string {
  if (size < 1024) return `${size} bytes`;

  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;

  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(2)} MB`;

  return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export async function streamToBuffer(stream: Readable): Promise<Buffer> {
  const chunks: Uint8Array[] = [];

  return new Promise((resolve, reject) => {
    stream.on('data', (chunk: Uint8Array) => chunks.push(chunk));
    stream.on('error', reject);
    stream.on('end', () => resolve(Buffer.concat(chunks)));
  });
}

export function chunkArray<T>(array: T[], chunkSize: number = 300): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

export function addressRows(address?: DanishAddress): string[] {
  if (!address) return [];

  const lines: string[] = [];

  // c/o line
  if (address.coName) {
    lines.push(`c/o ${address.coName}`);
  } else {
    lines.push('');
  }

  // Street line
  let streetLine = '';
  if (address.streetName) {
    streetLine += address.streetName;
  }
  if (address.houseNumber) {
    streetLine += ` ${address.houseNumber}`;
  }
  if (address.letter) {
    streetLine += address.letter;
  }
  if (streetLine) {
    // Floor and door (if present)
    const floorDoor = [address.floor ? `${address.floor}.` : '', address.door].filter(Boolean).join(' ');
    lines.push(floorDoor ? `${streetLine}, ${floorDoor}` : streetLine);
  } else {
    lines.push('');
  }

  // PO Box
  if (address.poBox) {
    lines.push(address.poBox);
  } else {
    lines.push('');
  }

  // Postal code and city
  const postalCity = [address.postalCode, address.city].filter(Boolean).join(' ');
  if (postalCity) {
    lines.push(postalCity);
  } else {
    lines.push('');
  }

  return lines;
}

export function formatDanishAddress(address?: DanishAddress): string {
  return addressRows(address)
    .filter((n) => n !== '')
    .join('\n');
}

export function getRoute(path: string) {
  const host = process.env.LUMA_HOST!;
  return `${host}${path}`;
}

export function languageFromLocale(locale: string) {
  switch (locale) {
    case 'da':
      return 'Danish';
    case 'en':
      return 'English';
    default:
      return 'English';
  }
}

export function lastDateOfCurrentMonth(locale: 'da' | 'en' = 'en'): string {
  const now = new Date();

  switch (locale) {
    case 'da':
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

    default:
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
  }
}

export function hasValue<T>(candidate: T | undefined | null): candidate is T {
  return candidate !== undefined && candidate !== null;
}
